// @LTG-ID: F-015
// @STATUS: BUILT
// @HERMETIC: false  // consumes ExternalCTA
// @DOCS: 08_FEATURES.md#F-015, 10_DECISIONS.md#ADR-012 (D5),
//        04b_SHARED_CONTRACT.md (membership-gating contract),
//        source Landing Page.html lines 2673-2744
// @CHANGED: 2026-02 session_003 (CHG-0044)
// @OWNER: web
//
// F-015 — Two-card product ladder: $995 Snapshot (primary, dark navy) +
// Growth Membership (locked, white). Per ADR-012 D5, the locked card's
// CTA is a real `<ExternalCTA>` rendered with `aria-disabled="true"` so
// screen-reader users get the lock signal while keyboard activation
// still works (server-side gating on the dashboard is the actual
// security boundary — see 04b_SHARED_CONTRACT.md).
//
// CHG-0101 — Removed the visible monthly price chip from the locked
// membership card per user directive. Pricing for the monthly engagement
// is owned by the dashboard signup flow at app.letstalkgrowth.com, not
// the marketing landing surface. The card's narrative (6-row feature
// list + "Unlocks after your Snapshot" CTA) stands without the chip.

import ExternalCTA from "../../lib/external-cta";
import ItalicPeriod from "../brand/ItalicPeriod";
import "./engagements.css";

export function Engagements() {
    return (
        <section
            className="ltg-engage-section"
            id="engage"
            data-testid="engagements-section"
        >
            <div className="ltg-container">
                <div className="ltg-engage__wrap">
                    <div
                        className="ltg-block-head"
                        style={{ marginBottom: 0 }}
                    >
                        <span
                            className="ltg-eyebrow"
                            data-testid="engagements-eyebrow"
                        >
                            How it works
                        </span>
                        <h2 data-testid="engagements-h2">
                            One report opens the door
                            <ItalicPeriod />
                            <br />
                            The rest unlocks when you're in.
                        </h2>
                    </div>

                    {/* CHG-0117 · trust-count eyebrow above the pricing card */}
                    <p
                        className="ltg-engage__count-eyebrow"
                        data-testid="pricing-count-eyebrow"
                    >
                        Trusted by 1200+ owner-operated businesses across the U.S.
                    </p>

                    {/* CHG-0117 · Block 1 · single pull quote directly above pricing card */}
                    <figure
                        className="ltg-engage__pullquote"
                        data-testid="testimonial-hero-pullquote"
                    >
                        <blockquote>
                            <p>
                                I expected a generic calculator output. This
                                was a real report, signed by a real CPA, with
                                three specific things I could do.
                            </p>
                        </blockquote>
                        <figcaption>
                            Owner, industrial services · Southeast U.S.
                        </figcaption>
                    </figure>

                    <div className="ltg-ladder" data-testid="engagements-ladder">
                        {/* Primary — $995 Strategic Snapshot */}
                        <div
                            className="ltg-ladder-card ltg-ladder-card--primary"
                            data-testid="engagements-snapshot-card"
                        >
                            <div className="ltg-ladder__step">
                                <span className="ltg-ladder__step-num">01</span>
                                <span className="ltg-ladder__step-label">
                                    Start here
                                </span>
                            </div>
                            <h3>Strategic Snapshot</h3>
                            <div className="ltg-ladder__price">
                                <span className="ltg-ladder__price-num">
                                    $995
                                </span>
                                <span className="ltg-ladder__price-sub">
                                    one-time · CPA-signed
                                </span>
                            </div>
                            <p className="ltg-ladder__desc">
                                A complete report on where your business
                                stands, what it is worth, and the three moves
                                worth making this year. Produced under the
                                KGOB methodology developed by Sara F.
                                Gonzalez, CPA.
                            </p>
                            <ul className="ltg-ladder__list">
                                <li>
                                    <span>Valuation range</span>
                                    <span>with full, citable methodology</span>
                                </li>
                                <li>
                                    <span>10-lever scorecard</span>
                                    <span>
                                        your business vs. ~3,400 owner-operated
                                        peers
                                    </span>
                                </li>
                                <li>
                                    <span>Three priority moves</span>
                                    <span>
                                        ranked by impact on enterprise value
                                    </span>
                                </li>
                                <li>
                                    <span>Tax-efficiency review</span>
                                    <span>federal + NC, signed by a CPA</span>
                                </li>
                                <li>
                                    <span>One round of Q&amp;A</span>
                                    <span>
                                        written, with Sara, within 3 business
                                        days
                                    </span>
                                </li>
                                {/* CHG-0107.2 — 14-day refund policy list item removed */}
                            </ul>
                            <ExternalCTA
                                ctaId="engagements-snapshot-order"
                                className="ltg-btn ltg-btn-teal ltg-ladder__cta"
                                data-testid="engagements-snapshot-order-cta"
                            >
                                Get Your Valuation{" "}
                                <span className="ltg-arrow" aria-hidden="true">
                                    →
                                </span>
                            </ExternalCTA>
                            <p className="ltg-ladder__fineprint">
                                Delivered as a watermarked PDF. Pay once.
                                Indicative valuation, not a USPAP appraisal
                                under SSVS No. 1.
                            </p>
                        </div>

                        {/* Locked — Growth Membership (monthly recurring;
                            price revealed in the dashboard signup flow at
                            app.letstalkgrowth.com — CHG-0101) */}
                        <div
                            className="ltg-ladder-card ltg-ladder-card--gated"
                            data-testid="engagements-membership-card"
                        >
                            <div className="ltg-ladder__step">
                                <span className="ltg-ladder__step-num">02</span>
                                <span className="ltg-ladder__step-label">
                                    When you're ready
                                </span>
                            </div>
                            <h3>Growth Membership</h3>
                            <div className="ltg-ladder__price ltg-ladder__price--link">
                                <ExternalCTA
                                    ctaId="engagements-monthly"
                                    className="ltg-ladder__monthly-link"
                                    data-testid="engagements-monthly-link"
                                >
                                    View monthly plans{" "}
                                    <span className="ltg-arrow" aria-hidden="true">
                                        →
                                    </span>
                                </ExternalCTA>
                            </div>
                            <p className="ltg-ladder__desc">
                                Available by invitation after your Snapshot.
                                The system that turns the report into a
                                quarter-by-quarter operating rhythm: tools,
                                journals, coaching, and expert Q&amp;A.
                            </p>
                            <ul className="ltg-ladder__list">
                                <li>
                                    <span>
                                        All KGOB tools, full versions
                                    </span>
                                    <span>
                                        Valuation Workbench, 32-question Exit
                                        Index, lever tracker
                                    </span>
                                </li>
                                <li>
                                    <span>The KGOB Journals archive</span>
                                    <span>
                                        full library · new playbooks each month
                                    </span>
                                </li>
                                <li>
                                    <span>Coaching content</span>
                                    <span>
                                        Sara's quarterly letter · recorded
                                        sessions
                                    </span>
                                </li>
                                <li>
                                    <span>Expert Q&amp;A</span>
                                    <span>
                                        written consultations with KGOB experts
                                    </span>
                                </li>
                                <li>
                                    <span>Quarterly Snapshot refresh</span>
                                    <span>
                                        watch the numbers move as you make
                                        moves
                                    </span>
                                </li>
                                <li>
                                    <span>Cancel any time</span>
                                    <span>
                                        no annual contract, no minimum
                                    </span>
                                </li>
                            </ul>
                            {/* ADR-012 D5: locked CTA is a real ExternalCTA
                                marked aria-disabled. Server enforces gating. */}
                            <ExternalCTA
                                ctaId="engagements-membership-locked"
                                className="ltg-btn ltg-ladder__locked"
                                aria-disabled="true"
                                data-testid="engagements-membership-locked-cta"
                            >
                                <span
                                    className="ltg-ladder__lock-icon"
                                    aria-hidden="true"
                                >
                                    ◎
                                </span>
                                Unlocks after your Snapshot
                            </ExternalCTA>
                            <p className="ltg-ladder__fineprint">
                                Custom scopes, sector deep-dives, and
                                in-office consultations available to members
                                on request as extras.
                            </p>
                        </div>
                    </div>

                    <p className="ltg-engage__footnote">
                        We do not run discovery calls. The Snapshot{" "}
                        <em>is</em> the conversation. On paper, signed, in
                        your inbox.
                    </p>
                </div>
            </div>
        </section>
    );
}

export default Engagements;
